// frontend/modules/internal-account/views/InternalAccountFormView.tsx
import React, { useEffect, useState } from 'react';
import { Card, Typography, Form, Input, Button, Space, Select, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { useInternalAccounts } from '../hooks/useInternalAccounts';
import { InternalAccountDTO } from '../stores/internal-account-store';

// Import the Orval generated API client for COA and Company
import { useGetLeafNodesWithoutSpecificProducts } from '@/shared/api/client/coa-resource';
import { useGetAllBranches } from '@/shared/api/client/company-resource';
import { useQueryClient } from '@tanstack/react-query';
import {CompanyDTO} from "@/shared/api/model";

const { Title } = Typography;
const { Option } = Select;

interface FormValues {
    branch: string;
    glNo: string;
    acNo: string;
    acName: string;
    acStatus: 'A' | 'C';
}

const InternalAccountFormView: React.FC = () => {
    const { dark } = useTheme();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const params = useParams<{ branch?: string; glNo?: string; acNo?: string }>();
    const [form] = Form.useForm<FormValues>();
    const [isEditing, setIsEditing] = useState(false);
    const [coaOptions, setCoaOptions] = useState<Array<{ glNo: string; acName: string }>>([]);
    const [branchOptions, setBranchOptions] = useState<Array<CompanyDTO>>([]);
    const [selectedGlNo, setSelectedGlNo] = useState<string | null>(null);
    const [isCheckingAccount, setIsCheckingAccount] = useState(false);

    const {
        isLoading,
        createAccount,
        updateAccount,
        checkAccountExists
    } = useInternalAccounts();

    // Fetch COA leaf nodes for GL selection
    const coaQuery = useGetLeafNodesWithoutSpecificProducts();

    // Fetch branches for branch selection
    const branchesQuery = useGetAllBranches();

    // Update COA options when data is loaded
    useEffect(() => {
        if (coaQuery.data) {
            // @ts-ignore
            setCoaOptions(coaQuery.data);
        }
    }, [coaQuery.data]);

    // Update branch options when data is loaded
    useEffect(() => {
        if (branchesQuery.data) {
            setBranchOptions(branchesQuery.data);
        }
    }, [branchesQuery.data]);

    // Determine if we're in edit mode based on URL params
    useEffect(() => {
        if (params.branch && params.glNo && params.acNo) {
            setIsEditing(true);
            // In a real app, we would fetch the account details here
            // For now, we'll just set some mock data
            form.setFieldsValue({
                branch: params.branch,
                glNo: params.glNo,
                acNo: params.acNo,
                acName: 'Mock Account Name',
                acStatus: 'A'
            });
            setSelectedGlNo(params.glNo);
        }
    }, [params, form]);

    // Handle form submission
    const handleSubmit = async (values: FormValues) => {
        const accountData: InternalAccountDTO = {
            branch: values.branch,
            glNo: values.glNo,
            acNo: values.acNo,
            acName: values.acName,
            acStatus: values.acStatus || 'A'
        };

        if (isEditing) {
            updateAccount(accountData);
        } else {
            // Check if account already exists
            setIsCheckingAccount(true);
            const exists = await checkAccountExists(values.branch, values.glNo, values.acNo);
            setIsCheckingAccount(false);
            
            if (exists) {
                form.setFields([
                    {
                        name: 'acNo',
                        errors: ['Account with this number already exists']
                    }
                ]);
                return;
            }
            
            createAccount(accountData);
        }
        
        // Navigate back to the list view
        navigate('/accounts/internal');
    };

    // Handle GL number selection
    const handleGlNoChange = (value: string) => {
        setSelectedGlNo(value);
        
        // Find the selected COA item
        const selectedCoa = coaOptions.find(coa => coa.glNo === value);
        if (selectedCoa) {
            // Suggest the account name based on the COA name
            form.setFieldValue('acName', selectedCoa.acName);
        }
    };

    // Handle cancel button
    const handleCancel = () => {
        navigate('/accounts/internal');
    };

    return (
        <Card className={`${dark ? 'bg-gray-800 text-white' : 'bg-white'} shadow-md`}>
            <div className="flex justify-between items-center mb-4">
                <Title level={4} className={dark ? 'text-white' : ''}>
                    {isEditing ? 'Edit Internal Account' : 'Create Internal Account'}
                </Title>
            </div>
            
            <Spin spinning={isLoading || isCheckingAccount || coaQuery.isLoading || branchesQuery.isLoading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ acStatus: 'A' }}
                >
                    <Form.Item
                        name="branch"
                        label="Branch"
                        rules={[
                            { required: true, message: 'Please select a branch' }
                        ]}
                    >
                        <Select 
                            showSearch
                            placeholder="Select branch"
                            optionFilterProp="children"
                            disabled={isEditing}
                            loading={branchesQuery.isLoading}
                        >
                            {branchOptions.map(branch => (
                                <Option key={branch.branch} value={branch.brName}>
                                    {branch.branch} - {branch.brName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    
                    <Form.Item
                        name="glNo"
                        label="GL Number"
                        rules={[
                            { required: true, message: 'Please select GL number' }
                        ]}
                    >
                        <Select 
                            showSearch
                            placeholder="Select GL number"
                            optionFilterProp="children"
                            onChange={handleGlNoChange}
                            disabled={isEditing}
                            loading={coaQuery.isLoading}
                        >
                            {coaOptions.map(coa => (
                                <Option key={coa.glNo} value={coa.glNo}>
                                    {coa.glNo} - {coa.acName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    
                    <Form.Item
                        name="acNo"
                        label="Account Number"
                        rules={[
                            { required: true, message: 'Please enter account number' },
                            { max: 6, message: 'Account number cannot exceed 6 characters' }
                        ]}
                    >
                        <Input maxLength={6} disabled={isEditing} />
                    </Form.Item>
                    
                    <Form.Item
                        name="acName"
                        label="Account Name"
                        rules={[
                            { required: true, message: 'Please enter account name' },
                            { max: 50, message: 'Account name cannot exceed 50 characters' }
                        ]}
                    >
                        <Input maxLength={50} />
                    </Form.Item>
                    
                    {isEditing && (
                        <Form.Item
                            name="acStatus"
                            label="Status"
                            rules={[
                                { required: true, message: 'Please select status' }
                            ]}
                        >
                            <Select>
                                <Option value="A">Active</Option>
                                <Option value="C">Closed</Option>
                            </Select>
                        </Form.Item>
                    )}
                    
                    <Form.Item>
                        <Space>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                icon={<SaveOutlined />}
                            >
                                {isEditing ? 'Update' : 'Create'}
                            </Button>
                            <Button 
                                onClick={handleCancel}
                                icon={<ArrowLeftOutlined />}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Spin>
        </Card>
    );
};

export default InternalAccountFormView;
